import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Typography, CircularProgress, Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ErrorIcon from "@material-ui/icons/Error";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SignalWifiOffIcon from "@material-ui/icons/SignalWifiOff";
import CheckIcon from "~/components/common/CheckIcon";
import GoogleIcon from "@mui/icons-material/Google";
import PhoneIcon from "@material-ui/icons/Phone";
import checkoutUseStyles from "~/components/checkout/styles";
import useStyles from "./styles";
import { callWithSupport } from "~/utils/business";
import { mixpanelTrackEvent } from "~/utils/mixpanel";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import checkoutServices from "~/services/checkoutServices";
import { useSelector } from "react-redux";
import _ from "lodash";
import Utils from "~/helpers/Utils";

const CODE_EXPIRATION_TIME = 30;

export default function DialogWhatsAppAuth({
	status = "PROCESS",
	errorMessage = null,
	onFinalize,
	loading = false,
	title,
	subtitle,
	buttonText,
	checkoutInformation = null,
	schedulingServicePackage = null,
	questions = [],
	observation = "",
	onFinalizeAppointment = null,
	observationTextField = null,
	setErrorObservation = null,
	onStartLoading = null,
	loginWithWhatsApp,
	showCustomLogin,
	showLoginGoogle,
	successRoute,
	onClose,
	handleFallback,
	goToFallbackBusinessPage = () => {},
	hideSuccessButton = false,
	bookingLoading = false,
}) {
	const { t } = useTranslation();
	const provider = new GoogleAuthProvider();
	const auth = getAuth();
	const classes = useStyles();
	const history = useHistory();
	const location = useLocation();
	const { business } = useSelector((store) => store.business);
	const { user } = useSelector((store) => store.auth);
	const meStore = useSelector((store) => store.me?.me);
	const me = Utils.safeParseJSON("@prit-web/meData", meStore);
	const currentUser = me || meStore?.me|| user || {};
	const isOwnerUser =
	!_.isEmpty(currentUser?.id) && !_.isEmpty(business?.ownerId) && currentUser.id === business.ownerId;
	const classesCheckout = checkoutUseStyles();
	const [timer, setTimer] = useState(CODE_EXPIRATION_TIME);
	const [showSuccessContent, setShowSuccessContent] = useState(false);
	const [showFallback, setShowFallback] = useState(false);
	const [goToFallback, setGoToFallback] = useState(false);
	const [goBack, setGoBack] = useState(false);

	const loginGoogle = async () => {
		try {
			mixpanelTrackEvent("Fallback_Whatsapp_Google", {}, currentUser, isOwnerUser ? business : {});
			await signInWithPopup(auth, provider);

			const res = await checkoutServices.me();

			if (res?.data?.id) {
				localStorage.setItem("@prit-web/meData", JSON.stringify(res.data));
				localStorage.setItem("@prit-web/userId", res.data.id);

				if (!res.data.phoneUnverified) {
					history.replace("/login/personal-information-number", {
						logEventFor: "Google",
						goBackRoute: "/checkout/" + business.id,
						goBackRouteProps: location.state,
					});
				} else {
					window.location.reload();
				}
			}
		} catch (error) {
			alert(`Erro durante o login com Google: ${error.message}`);
		}
	};

	const loginWithSms = () => {
		try {
			mixpanelTrackEvent("Fallback_Whatsapp_Telefone", {}, currentUser, isOwnerUser ? business : {});
			if (!me || !me?.name || (!me?.phone && !me?.phoneUnverified) || !user || user.isAnonymous) {
				localStorage.setItem("goBackRoute", JSON.stringify(successRoute));
				localStorage.setItem("@prit-web/checkoutAutoSchedule", JSON.stringify("sms"));
				history.push(`/login`, {
					goBackRoute: "/checkout/" + business.id,
					goBackRouteProps: location.state,
				});
				return;
			} 
		} catch (error) {
			console.error(error);
		}
	}

	const fallBackLogin = () => {
		setShowFallback(true);
		mixpanelTrackEvent("Consumidor_com_dificuldades_no_Whatsapp", {}, currentUser, isOwnerUser ? business : {})
	}

	useEffect(() => {

		if (!status && showCustomLogin) {
			setTimer(CODE_EXPIRATION_TIME);
			if (goBack || handleFallback) {
				setShowFallback(false);
			} else if (goToFallback) {
				setShowFallback(true);
			}
			return;
		}

		const shouldShowTimer =
			status === "PROCESS" ||
			status === "OK" ||
			status === "FINALIZING" ||
			(status !== "UNEXPECTED" && status !== "EXPIRED_CODE" && status !== "INVALID_MESSAGE");

		if (shouldShowTimer) {
			const interval = setInterval(() => {
				setTimer((prevTimer) => {
					if (prevTimer <= 1) {
						clearInterval(interval);
						return 0;
					}
					return prevTimer - 1;
				});
			}, 1000);

			return () => clearInterval(interval);
		} else {
			setTimer(CODE_EXPIRATION_TIME);
		}
	}, [status, handleFallback]);

	useEffect(() => {
		if (status === "SUCCESS") {
			setShowSuccessContent(false);
			const timer = setTimeout(() => {
				setShowSuccessContent(true);
			}, 650); 

			return () => clearTimeout(timer);
		} else {
			setShowSuccessContent(false);
		}
	}, [status]);

	const getStatusContent = (statusCustom) => {
		switch (statusCustom ?? status) {
			case "PROCESS":
				return {
					title: t("Waiting for WhatsApp to arrive"),
					showLoading: true,
					iconColor: "#25D366",
					showTimer: true,
					showTimerMessage: true,
					showCustomButton: true,
					customButton: () => (
						<Button
							onClick={() => {
								onClose();
								setGoBack(true);
							}}
							className={classes.fallbackButton}
						>
							{t("Go back to login")}
						</Button>
					)
				};
			case "UNEXPECTED":
				return {
					title: t("Unexpected error"),
					message: t("An unexpected error occurred during authentication Please try again"),
					showLoading: false,
					iconColor: "#f44336",
					showTimer: false,
					showTimerMessage: false,
				};
			case "CONNECTION_ERROR":
				return {
					title: t("connectionProblem"),
					message: t("unableToConnectCheckInternet"),
					showLoading: false,
					iconColor: "#ff9800",
					showTimer: false,
					showTimerMessage: false,
					isConnectionError: true,
				};
			case "EXPIRED_CODE":
				return {
					title: t("Code expired"),
					message: t("The authentication code has expired Please try again"),
					showLoading: false,
					iconColor: "#ff9800",
					showTimer: false,
					showTimerMessage: false,
				};
			case "INVALID_MESSAGE":
				return {
					title: t("Invalid message"),
					message: t("The authentication message is invalid Please try again"),
					showLoading: false,
					iconColor: "#f44336",
					showTimer: false,
					showTimerMessage: false,
				};
			case "OK":
				return {
					title: t("Authentication successful"),
					message: t("Authentication completed successfully"),
					showLoading: false,
					iconColor: "#4caf50",
					showTimer: false,
					showTimerMessage: false,
				};
			case "FINALIZING":
				return {
					title: t("Validating your phone"),
					message: t("We are completing the verification Please wait"),
					showLoading: true,
					iconColor: "#25D366",
					showTimer: true,
					showTimerMessage: false,
				};
			case "SUCCESS":
				return {
					title: title || t("Phone validated successfully"),
					message: subtitle || t("Now you can finalize your appointment"),
					buttonText: buttonText || t("Finalize appointment"),
					showLoading: false,
					iconColor: "#4caf50",
					showButton: true,
				};
			case "ERROR":
				return {
					title: t("Authentication error"),
					message: errorMessage || t("An error occurred during authentication Please try again"),
					showLoading: false,
					iconColor: "#f44336",
					showTimer: false,
					showTimerMessage: false,
				};
			case "FALLBACK_BEFORE_20_SECONDS":
				return {
					title: t("Having trouble with WhatsApp"),
					message: t("You can try another way to log in"),
					showLoading: false,
					iconColor: "#25D366",
					showCustomButton: true,
					showTimer: false,
					showLoading: true,
					customButton: () => (
						<Button
							onClick={() => {
								onClose();
								goToFallbackBusinessPage(true);
								setGoToFallback(true);
								setShowFallback(true);
							}}
							className={classes.fallbackButton}
						>
							{t("Sign in another way")}
						</Button>
					)
				}
			default:
				return {
					title: t("Waiting for WhatsApp to arrive"),
					message: t(
						"We opened your WhatsApp with a ready message for you to send We are waiting for WhatsApp to arrive"
					),
					showLoading: true,
					iconColor: "#25D366",
					showTimer: true,
					showTimerMessage: false,
				};
		}
	};

	const content = timer <= 10 ? getStatusContent("FALLBACK_BEFORE_20_SECONDS") : getStatusContent();
	const isError =
		status === "UNEXPECTED" || status === "EXPIRED_CODE" || status === "INVALID_MESSAGE" || status === "ERROR";
	const isSuccess = status === "OK";
	const isConnectionError = status === "CONNECTION_ERROR";
	const isSuccessValidatePhone = status === "SUCCESS";

	const hasUnansweredQuestions = !schedulingServicePackage && questions?.length > 0 && observation === "";
	const needsPayment =
		!schedulingServicePackage &&
		checkoutInformation &&
		!checkoutInformation?.use_cancellation_policy &&
		(checkoutInformation?.anticipation_payment_percentage > 0 ||
			checkoutInformation?.anticipation_payment > 0 ||
			checkoutInformation?.used_percentage_value ||
			checkoutInformation?.used_fixed_value);

	useEffect(() => {
		if (status === "SUCCESS" && hasUnansweredQuestions && !needsPayment) {
			if (onClose) onClose();
			if (successRoute) history.push(successRoute);
		}
	}, [status, hasUnansweredQuestions, needsPayment]);

	const handleFinalize = () => {
		if (loading) return;

		if (checkoutInformation && !checkoutInformation?.use_cancellation_policy && onFinalizeAppointment) {
			if (!needsPayment && !hasUnansweredQuestions) {
				if (onStartLoading) {
					onStartLoading();
				}
				onFinalizeAppointment();
			} else {
				if (onFinalize) {
					onFinalize();
				}
			}
		} else if (checkoutInformation?.use_cancellation_policy) {
			if (onFinalize) {
				onFinalize();
			}
		} else {
			if (onFinalize) {
				onFinalize();
			}
		}
	};

	if (!status && showCustomLogin) {
		return (
			<>
				{!showFallback ? (
					<>
						<div className={classes.loginContainer}>
							<Typography className={classes.verifyPhoneTitle}>{t("Verify phone number")}</Typography>
							<Typography className={classes.verifyPhoneSubtitle}>
								{t("Before finishing your booking we need to verify your phone number")}
							</Typography>
						</div>
						<Button className={classes.button} style={{ textTransform: "none" }} disabled={loading} 
							onClick={() => {
								loginWithWhatsApp(); 
							}}
						>
							<WhatsAppIcon className={classes.whatsappButtonIcon} />
							<span className={classes.whatsappButtonSpan}>{t("whatsApp login soon")}</span>
						</Button>
						<div className={classes.supportContainer}>
							<Typography
								onClick={() => {
									setShowFallback(true);
									fallBackLogin();
								}}
								className={classes.supportText}>
								{t("whatsappLoginDifficultyMessage")}
							</Typography>
						</div>
					</>
				) : (
					<>
						<div className={classes.loginContainerCentered}>
							<Typography className={classes.phoneConfirmationText}>
								{t("whatsappLoginPhoneConfirmationMessage")}
							</Typography>
							{process.env.REACT_APP_GOOGLE_LOGIN_ENABLED === "true" && showLoginGoogle && (
								<Button
									disabled={loading}
									onClick={loginGoogle}
									className={`${classesCheckout.buttonChooseLogin} ${classes.googleButton} ${
										loading ? classes.googleButtonLoading : ""
									}`}>
									<GoogleIcon className={classes.googleIconPosition} />
									<span className={classes.googleButtonSpan}>{t("Continue Google")}</span>
								</Button>
							)}
							<Button
								disabled={loading}
								onClick={() => loginWithSms()}
								className={`${classesCheckout.buttonChooseLogin} ${classes.phoneButton} ${
									loading ? classes.phoneButtonLoading : ""
								}`}>
								<PhoneIcon className={classes.phoneIconPosition} />
								<Typography className={classes.phoneButtonText}>{t("Continue")}</Typography>
							</Button>
							<div className={classes.supportContainer}>
								<Typography
									onClick={() => callWithSupport(t("whatsappSupportLoginMessage"), t)}
									className={classes.supportText}>
									{t("whatsappLoginSupportMessage")}
								</Typography>
							</div>
						</div>
					</>
				)}
			</>
		);
	}

	return (
		<div className={classes.dialogContainer}>
			<header className={`${classes.dialogHeader} ${status === "SUCCESS" ? classes.dialogHeaderSuccess : ""}`}>
				<div className={isSuccessValidatePhone ? classes.successCheckContainer : classes.iconContainer}>
					{isConnectionError && <SignalWifiOffIcon style={{ fontSize: 48, color: content.iconColor }} />}
					{isError && <ErrorIcon style={{ fontSize: 48, color: content.iconColor }} />}
					{isSuccess && !isSuccessValidatePhone && (
						<CheckCircleIcon style={{ fontSize: 48, color: content.iconColor }} />
					)}
					{isSuccessValidatePhone && (
						<div>
							<CheckIcon size={120} />
						</div>
					)}
				</div>

				{content.showLoading && (
					<div className={classes.loadingContainer}>
						<CircularProgress size={45} style={{ color: content.iconColor }} />
					</div>
				)}

				<div
					className={
						status === "SUCCESS" && !showSuccessContent
							? classes.successContentContainerHidden
							: classes.successContentContainer
					}>
					{(status !== "SUCCESS" || (status === "SUCCESS" && showSuccessContent)) && (
						<>
							<Typography
								className={classes.titleText}
								style={{
									marginTop: content.showLoading ? 40 : 0,
								}}>
								{content.title}
							</Typography>

							<Typography className={classes.messageText}>{content.message}</Typography>
						</>
					)}
				</div>

				{content.showLoading && content.showTimer && status !== "FINALIZING" && (
					<Typography className={classes.timerText}>{t("Try again in {{seconds}}s", { seconds: timer })}</Typography>
				)}

				{isConnectionError && (
					<Typography className={classes.connectionErrorText}>{t("thisWindowWillCloseAutomatically")}</Typography>
				)}

				<div
					className={
						status === "SUCCESS" && !showSuccessContent
							? classes.successContentContainerHidden
							: classes.successContentContainer
					}>
					{status === "SUCCESS" && showSuccessContent && bookingLoading && (
						<CircularProgress size={28} style={{ color: "#6b2030", marginTop: 8 }} />
					)}

					{status === "SUCCESS" && showSuccessContent && content.showButton && !hasUnansweredQuestions && !hideSuccessButton && !bookingLoading && (
						<Button
							onClick={handleFinalize}
							className={`${classes.finalizeButton} ${loading ? classes.finalizeButtonLoading : ""}`}
							disabled={loading}>
							{loading && <CircularProgress size={20} className={classes.loadingIcon} />}
							{content.buttonText}
						</Button>
					)}

					{ (content?.showCustomButton && content?.customButton) && (
						<div className={classes.customLoginContainer}>
							{content.customButton()}
						</div>
					) }
				</div>
			</header>
			<style>
				{`
					@keyframes slideInFromRight {
						from {
							opacity: 0;
							transform: translateX(40px);
						}
						to {
							opacity: 1;
							transform: translateX(0);
						}
					}

					@keyframes slideInFromBottom {
						from {
							opacity: 0;
							transform: translateY(40px);
						}
						to {
							opacity: 1;
							transform: translateY(0);
						}
					}

					@keyframes slideOutToLeft {
						from {
							opacity: 1;
							transform: translateX(0);
						}
						to {
							opacity: 0;
							transform: translateX(-30px);
						}
					}
				`}
			</style>
		</div>
	);
}
